import { randomUuid, sha256HexNow } from './browser-crypto';
import type {
  IdentityLoginCompletedResponse,
  IdentityLoginRequest,
  IdentityLoginResponse,
} from '@exylon/generated-contract-kernel/types';
import { isWorkspaceGranted, type PanelAccessModel } from '../foundation/access';
import { AccountVerificationPendingError, type AccountRuntimePort } from '../foundation/account-port';
import type { AuthenticationOutcome, CredentialRequest, PanelGeneratedHttpPort, PanelIdentityRuntimePort, PanelLiveScreenId, PanelLoginMembershipChoice, PanelLoginMembershipSelection, PanelRememberedScreenId, PanelScreenMemory, PanelScreenMemoryLease, PanelScreenRuntimeAccess, ReauthenticationRequest } from '../foundation/runtime-port';
import { createAccountRuntimePort } from './account-adapter';
import { panelDurableWork, type DurableWorkStore } from './durable-work';
import { IdentityHttpClient, IdentityHttpError } from './identity-http';
import { projectCredentialSession, projectRuntimeSession, type PanelCredentialSession, type PanelRuntimeSession, type RuntimeTenant } from './session-model';
import { PANEL_KEEPALIVE_OPERATIONS, PANEL_OPERATION_TIMEOUTS_MS, PANEL_PUBLIC_CLOCK_OPERATION, PANEL_SCREEN_HTTP_OPERATIONS } from './panel-screens';
import { authorityDigest, createScreenClient, ScreenReadFence, type ScreenClientHost } from './screen-clients';
import { REMEMBERED_SCREENS, ScreenMemoryStore, screenMemories } from './screen-memory';
import { SessionRecovery } from './session-recovery';
import {
  isInvalidResponse, isUnauthorized, isWorkspacePlacementMissing, reasonFor, refusalKind, screenSession, tokenValue,
  type CredentialRotationOutcome, type CredentialRotationRequest, type ExpectedTenant, type IdentityRuntimeState,
  type RuntimeFailureReason, type RuntimeOperation, type RuntimeUnavailableReason, type SessionIdentity,
} from './session-state';
import type { SessionTokenStore } from './session-token-store';

type ResolvedIdentityLoginResult =
  | IdentityLoginCompletedResponse['result']
  | Extract<IdentityLoginResponse['result'], Readonly<{ requiresTwoFactor: true }>>;

export interface IdentityRuntimeOptions {
  readonly baseUrl: string;
  readonly tokenStore: SessionTokenStore;
  readonly fetch?: typeof fetch;
  /** The clock the session's instants are judged on; the server's, as the answers' `Date` gives it, by default. */
  readonly now?: () => number;
  readonly idempotencyKey?: () => string;
  /** How long a screen read waits for a foreground verification before it
   * reports `session_verifying`. Mutations never wait. */
  readonly verificationReadWaitMs?: number;
  /** The work that outlives a screen, purged at logout and at a change of person. */
  readonly durableWork?: Pick<DurableWorkStore, 'purge'>;
}

/** One transport timeout: a verification slower than this is reported, not awaited. */
const VERIFICATION_READ_WAIT_MS = 15_000;

export class PanelIdentityRuntime implements PanelIdentityRuntimePort, PanelScreenRuntimeAccess {
  private state: IdentityRuntimeState = { kind: 'restoring' };
  private token: string | null = null;
  /** The screen lifetime of one principal: a new login, a logout or a local
   * abandonment advances it; renewal and reauthentication of the same person
   * do not, so a screen keeps its client, draft and retained commands. */
  private epoch = 0;
  /** Advances with every password login, including the same person's
   * reauthentication. It fences the account port and a renewal still in
   * flight, which belong to the bearer they started with. */
  private credentialGeneration = 0;
  private projectionGeneration = 0;
  private pendingAuthentication: Promise<AuthenticationOutcome> | null = null;
  private pendingHydration: Promise<void> | null = null;
  private pendingRenewal: Promise<void> | null = null;
  private pendingLogout: Promise<void> | null = null;
  private pendingCredentialUpdate: Promise<CredentialRotationOutcome> | null = null;
  private membershipSelectionProof: string | null = null;
  private interactionBlock: 'offline' | 'verification' | null = null;
  private renewalKey: string | null = null;
  /** When Identity last confirmed the ready session (its current read answered), on `now`. */
  private confirmedAt: number | null = null;
  private accountEpoch = -1;
  private accountCredentialGeneration = -1;
  private accountPort: AccountRuntimePort | null = null;
  private screenEpoch = -1;
  private readonly screenClients = new Map<PanelLiveScreenId, PanelGeneratedHttpPort>();
  private readonly screenMemory = new ScreenMemoryStore();
  /** Whose durable work this device may still hold; null before the first session of this page. */
  private workPrincipal: string | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly recovery = new SessionRecovery({ state: () => this.state, renew: () => this.renew(), rehydrate: () => this.rehydrateSession() });
  /** The screen reads on the wire or waiting for admission. */
  private readonly screenReads: ScreenReadFence;
  private readonly now: () => number;
  private readonly key: () => string;
  private readonly durableWork: Pick<DurableWorkStore, 'purge'>;
  private readonly http: IdentityHttpClient;

  constructor(private readonly options: IdentityRuntimeOptions) {
    // The session's instants are the server's: they are judged on its clock as this device reads it.
    this.now = options.now ?? (() => this.http.serverNow());
    this.key = options.idempotencyKey ?? (() => randomUuid());
    this.screenReads = new ScreenReadFence(() => this.interactionBlock, options.verificationReadWaitMs ?? VERIFICATION_READ_WAIT_MS);
    this.durableWork = options.durableWork ?? panelDurableWork;
    this.http = new IdentityHttpClient({ baseUrl: options.baseUrl, getToken: () => this.token,
      additionalOperations: PANEL_SCREEN_HTTP_OPERATIONS,
      operationTimeoutsMs: PANEL_OPERATION_TIMEOUTS_MS,
      keepaliveOperations: PANEL_KEEPALIVE_OPERATIONS,
      ...(options.fetch ? { fetch: options.fetch } : {}) });
  }

  /**
   * The clock the session's window is judged on: the server's, as this device reads it (`IdentityHttpClient.serverNow`).
   * A session's `expires_at` is compared with this, never with the device's own clock, which may run ahead or behind.
   *
   * The estimate starts at the device clock (offset 0) and no judgement is made on that start: the first `expires_at`
   * this page holds comes in the fresh `GET /auth/current` answer of `hydrate`, whose `Date` sets the offset before
   * `hydrate` compares anything, and every later judgement (`expire`, `renew`, the expiry timer, the activity renewal,
   * `sessionConfirmedWithin`) needs the `ready` or `credential_required` state that only that comparison commits. A
   * reload on a device ahead of the server therefore opens on the server's clock. Only an answer whose `Date` cannot be
   * read (an API that does not expose it across origins) leaves the device clock, which ends a window early, never late;
   * the server stays the authority either way: it refuses an ended bearer (401) and renews only inside its own window.
   */
  serverNow = (): number => this.now();

  /**
   * docs/decisions/2026-10-28-tpv-sin-conexion.md · the server's instant without a session: the TPV's probe when no
   * valid bearer exists (the public operation is sent without one). It throws the transport's error.
   */
  readPublicClock = async (): Promise<Readonly<{ server_time: string }>> => {
    const answer = await this.http.read({ operation: PANEL_PUBLIC_CLOCK_OPERATION, params: {} });
    return (answer as unknown as Readonly<{ result: Readonly<{ server_time: string }> }>).result;
  };

  /**
   * docs/decisions/2026-10-31-tpv-offline-without-a-caja-tablet.md · a digest of the bearer this tab holds (in memory,
   * else the tab's store), or null without one. It binds the TPV's offline data on this device to this very session
   * (the offline door opens only for it) without keeping the bearer anywhere else; it proves nothing to the server.
   */
  localSessionDigest = (): string | null => {
    let token = this.token;
    if (token === null) {
      try { const stored = this.options.tokenStore.read(); token = stored ? tokenValue(stored) : null; } catch { token = null; }
    }
    return token === null ? null : sha256HexNow(`exylon.panel-session:${token}`);
  };

  /** A screen receives neither the bearer nor an unrestricted HTTP client (`./screen-clients.ts`). */
  screenClient = (screen: PanelLiveScreenId): PanelGeneratedHttpPort => {
    if (this.screenEpoch !== this.epoch) {
      this.screenEpoch = this.epoch;
      this.screenClients.clear();
    }
    const existing = this.screenClients.get(screen);
    if (existing) return existing;
    const epoch = this.epoch;
    const client = createScreenClient(screen, this.screenClientHost);
    this.screenClients.set(screen, client);
    if (REMEMBERED_SCREENS.has(screen)) screenMemories.set(client, this.screenMemory.memoryFor(screen, () => epoch === this.epoch));
    return client;
  };

  private readonly screenClientHost: ScreenClientHost = {
    http: () => this.http,
    epoch: () => this.epoch,
    token: () => this.token,
    state: () => this.state,
    interactionBlock: () => this.interactionBlock,
    verificationSettled: (signal) => this.screenReads.verificationSettled(signal),
    identitySettling: () => Boolean(this.interactionBlock || this.pendingHydration || this.pendingRenewal
      || this.pendingLogout || this.pendingAuthentication || this.renewalKey),
    settleIdentity: async () => {
      if (this.pendingAuthentication) await this.pendingAuthentication;
      if (this.pendingLogout) await this.pendingLogout;
      else if (this.pendingRenewal) await this.pendingRenewal;
      else if (this.renewalKey) await this.renew();
      if (this.pendingHydration) await this.pendingHydration;
    },
    expireIfDue: () => this.expire(),
    expireSession: (session) => this.commit({ kind: 'expired', session }),
    trackRead: (read) => this.screenReads.track(read),
  };

  get account(): AccountRuntimePort {
    if (!this.accountPort || this.accountEpoch !== this.epoch || this.accountCredentialGeneration !== this.credentialGeneration) {
      const epoch = this.epoch;
      const credentialGeneration = this.credentialGeneration;
      const isCurrent = () => epoch === this.epoch && credentialGeneration === this.credentialGeneration;
      this.accountEpoch = epoch;
      this.accountCredentialGeneration = credentialGeneration;
      this.accountPort = createAccountRuntimePort(this.http, () => {
        if (isCurrent()) this.clearLocalSession();
      }, {
        isCurrent,
        beforeRequest: async () => {
          if (this.interactionBlock === 'offline') throw new IdentityHttpError('network');
          if (this.interactionBlock) throw new AccountVerificationPendingError();
          if (this.state.kind === 'credential_required') throw new IdentityHttpError('permission');
          if (this.pendingAuthentication) await this.pendingAuthentication;
          if (this.pendingCredentialUpdate) await this.pendingCredentialUpdate;
          if (this.pendingLogout) await this.pendingLogout;
          else if (this.pendingRenewal) await this.pendingRenewal;
          else if (this.renewalKey) await this.renew();
          if (this.pendingHydration) await this.pendingHydration;
          if (this.state.kind === 'unavailable') throw new IdentityHttpError(refusalKind(this.state.reason));
          if (this.state.kind !== 'ready') throw new IdentityHttpError('unauthenticated');
        },
        canDispatch: () => {
          if (this.interactionBlock === 'offline') throw new IdentityHttpError('network');
          return !this.interactionBlock && this.state.kind === 'ready'
            && !this.pendingAuthentication && !this.pendingHydration && !this.pendingRenewal
            && !this.pendingLogout && !this.pendingCredentialUpdate && !this.renewalKey;
        },
      });
    }
    return this.accountPort;
  }

  getSnapshot = (): IdentityRuntimeState => this.state;
  /** Connectivity/foreground presentation can stop work, never grant access. */
  setInteractionFrozen = (frozen: boolean, reason: 'offline' | 'verification' = 'verification'): void => {
    this.interactionBlock = frozen ? reason : null;
    if (this.interactionBlock === 'offline') this.screenReads.retire(() => new IdentityHttpError('network'));
    if (this.interactionBlock === 'verification') return;
    // Lifted (or turned offline): every read waiting on the verification
    // re-checks the fence itself; offline still refuses it as `network`.
    this.screenReads.releaseVerificationWaiters();
  };
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
  private commit(state: IdentityRuntimeState) {
    if (state.kind !== 'ready') this.confirmedAt = null;
    this.state = state;
    // A remembered board belongs to the principal and the grants it was read under.
    const session = screenSession(state);
    this.screenMemory.authorityChanged(session === null ? null : authorityDigest(session));
    // A new person on this page: the work of anyone else on this device goes, whatever its tier.
    const principal = session?.canonical.user.id ?? null;
    if (principal !== null && principal !== this.workPrincipal) {
      this.workPrincipal = principal;
      void this.durableWork.purge({ keepPrincipalId: principal });
    }
    this.recovery.schedule();
    this.listeners.forEach((listener) => listener());
  }

  watchSessionRecovery = (): (() => void) => this.recovery.watch();
  private previousSession(): SessionIdentity | null {
    return this.state.kind === 'credential_required' ? this.state.credential : 'session' in this.state ? this.state.session : null;
  }
  private saveToken(value: string) {
    this.token = tokenValue(value);
    // Storage denial does not invent a logout: the valid bearer stays in memory.
    try { this.options.tokenStore.write(this.token); } catch { /* Reload will require login. */ }
  }
  /** Ends the current screen lifetime without committing a state: every
   * screen client and account port of it refuses from now on. */
  private retireScreenLifetime(): number {
    this.epoch += 1;
    this.screenMemory.forget();
    this.screenReads.retire(() => new IdentityHttpError('permission'));
    return this.epoch;
  }
  private clearLocalSession(localOnly = false) {
    this.retireScreenLifetime();
    this.workPrincipal = null;
    void this.durableWork.purge({ keepPrincipalId: null });
    this.token = null;
    this.renewalKey = null;
    this.membershipSelectionProof = null;
    try { this.options.tokenStore.clear(); } catch { /* Never restore without server validation. */ }
    this.commit({ kind: 'signed_out', ...(localOnly ? { localOnly: true as const } : {}) });
  }
  private fail(error: unknown, operation: RuntimeOperation, previous: SessionIdentity | null) {
    const verified = this.state.kind === 'unavailable' && this.state.verifiedCredentialScope
      ? { verifiedCredentialScope: this.state.verifiedCredentialScope } : {};
    if (previous && 'rotationRequired' in previous) {
      this.commit(isUnauthorized(error) && operation !== 'logout'
        ? { kind: 'credential_required', credential: previous, status: 'expired' }
        : { kind: 'credential_required', credential: previous, status: 'unavailable',
          reason: isUnauthorized(error) ? 'permission' : reasonFor(error), operation });
      return;
    }
    if (isUnauthorized(error)) {
      // A refusal is not a logout receipt (an uncertain renewal may already
      // have rotated the bearer). Never report revocation on a 401.
      if (operation === 'logout') this.commit({ kind: 'unavailable', reason: 'permission', session: previous, operation, ...verified });
      else if (previous) this.commit({ kind: 'expired', session: previous });
      else this.clearLocalSession();
    } else this.commit({ kind: 'unavailable', reason: reasonFor(error), session: previous, operation, ...verified });
  }

  private async hydrate(epoch: number, generation: number, expectedPrincipal?: string, expectedTenant?: ExpectedTenant): Promise<void> {
    const verifiedCredentialScope = this.state.kind === 'unavailable' ? this.state.verifiedCredentialScope : undefined;
    expectedPrincipal ??= verifiedCredentialScope?.principalId;
    expectedTenant ??= verifiedCredentialScope?.tenant;
    const { result: { workspaces: included, ...canonical } } = await this.http.read({ operation: 'GET /auth/current', params: {} });
    const current = { result: canonical };
    if (epoch !== this.epoch || generation !== this.projectionGeneration) return;
    if (expectedPrincipal && current.result.user.id !== expectedPrincipal) throw new IdentityHttpError('invalid_response');
    if (expectedTenant && (current.result.session.membership_tenant_type !== expectedTenant.kind
      || expectedTenant.id !== undefined && current.result.session.membership_tenant_id !== expectedTenant.id)) throw new IdentityHttpError('invalid_response');
    // Required provenance is checked before any workspace read. Missing is not
    // a permanent credential, even if an older response validator admitted it.
    if (typeof current.result.user.rotation_required !== 'boolean') throw new IdentityHttpError('invalid_response');
    if (current.result.user.rotation_required) {
      const credential = projectCredentialSession(current.result);
      this.commit({ kind: 'credential_required', credential,
        status: Date.parse(credential.canonical.session.expires_at) <= this.now() ? 'expired' : 'required' });
      return;
    }
    // docs/decisions/2026-10-17-session-recheck-skipped-within-two-minutes.md: the current-session read answers the
    // workspaces of a granted restaurant member, so the Panel opens with one Identity read. Without them (a refusal to
    // explain, or an older Identity) the workspace read answers as before.
    const restaurantMember = current.result.session.membership_tenant_type === 'restaurant';
    const workspaces = restaurantMember && included !== undefined
      ? { statusCode: 200 as const, success: true as const, result: included }
      : restaurantMember
      ? await this.http.read({ operation: 'GET /me/workspaces', params: {}, query: { restaurant_id: current.result.session.membership_tenant_id } }).catch((error: unknown) => {
        const unassigned = isWorkspacePlacementMissing(error);
        if (epoch === this.epoch && generation === this.projectionGeneration
          && (unassigned || this.state.kind === 'credential_required' || verifiedCredentialScope)
          && error instanceof IdentityHttpError && error.status === 403) {
          // Current has confirmed credential completion, not workspace access.
          // Keep only retry identity binding; never retain a stale rotation gate or grant a shell.
          // An unplaced member (OD-1) is bound the same way, so «Volver a comprobar»
          // re-reads this person's access once a manager has placed them.
          this.commit({ kind: 'unavailable', reason: unassigned ? 'unassigned' : 'permission', session: null, operation: 'hydrate',
            verifiedCredentialScope: { principalId: current.result.user.id,
              tenant: { kind: current.result.session.membership_tenant_type, id: current.result.session.membership_tenant_id } } });
          return undefined;
        }
        throw error;
      })
      : undefined;
    if (current.result.session.membership_tenant_type === 'restaurant' && !workspaces) return;
    if (epoch !== this.epoch || generation !== this.projectionGeneration) return;
    const session = this.retainingAuthority(projectRuntimeSession(current.result, workspaces));
    if (Date.parse(session.canonical.session.expires_at) <= this.now()) { this.commit({ kind: 'expired', session }); return; }
    this.confirmedAt = this.now();
    // A re-check that confirmed the very session on screen changes nothing: no commit, no re-render.
    if (this.state.kind === 'ready' && this.state.session === session) return;
    this.commit({ kind: 'ready', session });
  }

  /** A re-verification that confirms the same grants hands screens the same
   * tenant and access objects, so nothing keyed on them restarts; one that
   * confirms the same canonical session hands back the session itself. */
  private retainingAuthority(session: PanelRuntimeSession): PanelRuntimeSession {
    const previous = this.state.kind === 'ready' || this.state.kind === 'expired' || this.state.kind === 'unavailable' ? this.state.session : null;
    if (!previous || authorityDigest(previous) !== authorityDigest(session)) return session;
    return previous.personName === session.personName && previous.identifier === session.identifier
      && JSON.stringify(previous.canonical) === JSON.stringify(session.canonical) ? previous : { ...session, tenant: previous.tenant };
  }

  /**
   * docs/decisions/2026-10-17-session-recheck-skipped-within-two-minutes.md: whether Identity confirmed the ready
   * session within the last `withinMs`. A foreground return inside that window skips its re-check; the Backend still
   * authorizes every request.
   */
  sessionConfirmedWithin = (withinMs: number): boolean => this.state.kind === 'ready' && this.confirmedAt !== null
    && this.now() - this.confirmedAt < withinMs;

  rehydrateSession = (): Promise<void> => {
    // A password continuation owns the next projection. Foreground work must
    // not read its predecessor bearer or take over its hydration generation.
    if (this.pendingAuthentication) return this.pendingAuthentication.then(() => undefined);
    if (this.pendingLogout) return this.pendingLogout;
    if (this.pendingCredentialUpdate) return this.pendingCredentialUpdate.then(() => undefined);
    if (this.pendingRenewal) return this.pendingRenewal;
    // A lost renewal response may have revoked this bearer. Only the same-key
    // renewal replay can recover its successor; a plain read cannot do so.
    if (this.renewalKey) return this.renew();
    if (this.pendingHydration) return this.pendingHydration;
    const epoch = this.epoch;
    const generation = ++this.projectionGeneration;
    const previous = this.previousSession();
    const task = (async () => {
      if (!this.token) {
        try { const stored = this.options.tokenStore.read(); this.token = stored ? tokenValue(stored) : null; }
        catch { this.token = null; }
      }
      if (!this.token) { if (epoch === this.epoch) this.commit({ kind: 'signed_out' }); return; }
      try { await this.hydrate(epoch, generation, previous?.canonical.user.id, previous?.tenant); }
      catch (error) { if (epoch === this.epoch && generation === this.projectionGeneration) this.fail(error, 'hydrate', previous); }
    })();
    this.pendingHydration = task;
    void task.finally(() => { if (this.pendingHydration === task) this.pendingHydration = null; });
    return task;
  };

  private trackAuthentication(task: Promise<AuthenticationOutcome>): Promise<AuthenticationOutcome> {
    this.pendingAuthentication = task;
    void task.finally(() => { if (this.pendingAuthentication === task) this.pendingAuthentication = null; });
    return task;
  }

  private async acceptLoginResult(
    result: ResolvedIdentityLoginResult,
    epoch: number,
    generation: number,
    previous: SessionIdentity | null,
    isCurrent: () => boolean,
  ): Promise<AuthenticationOutcome> {
    if (result.requiresTwoFactor) return { kind: 'second_factor_required' };
    if (typeof result.user.rotation_required !== 'boolean') throw new IdentityHttpError('invalid_response');
    if (previous && result.user.id !== previous.canonical.user.id) throw new IdentityHttpError('invalid_response');
    if (result.restaurant && result.franchise) throw new IdentityHttpError('invalid_response');
    const loginTenant: ExpectedTenant = result.restaurant
      ? { kind: 'restaurant', id: result.restaurant.id }
      : result.franchise ? { kind: 'franchise', id: result.franchise.id } : { kind: 'platform' };
    if (loginTenant.kind === 'platform' && !result.user.roles.includes('SUPERADMIN')) throw new IdentityHttpError('invalid_response');
    if (previous && (previous.tenant.kind !== loginTenant.kind || loginTenant.id !== undefined && previous.tenant.id !== loginTenant.id)) {
      throw new IdentityHttpError('invalid_response');
    }
    this.membershipSelectionProof = null;
    this.saveToken(result.token);
    try { await this.hydrate(epoch, generation, result.user.id, previous?.tenant ?? loginTenant); }
    catch (error) {
      if (isCurrent()) {
        // The new bearer answered for another principal or tenant: it never
        // inherits the screen lifetime kept for the same person.
        if (previous && isInvalidResponse(error)) this.retireScreenLifetime();
        this.fail(error, 'hydrate', previous);
      }
      return { kind: 'unavailable', reason: isUnauthorized(error) ? 'permission' : reasonFor(error) };
    }
    if (!isCurrent()) return { kind: 'unavailable', reason: 'server' };
    if (this.state.kind === 'expired'
      || this.state.kind === 'credential_required' && this.state.status === 'expired') {
      return { kind: 'unavailable', reason: 'expired_session' };
    }
    return this.state.kind === 'ready'
      || this.state.kind === 'credential_required' && this.state.status === 'required'
      ? { kind: 'authenticated' } : { kind: 'unavailable', reason: 'permission' };
  }

  private async login(body: IdentityLoginRequest, previous: SessionIdentity | null): Promise<AuthenticationOutcome> {
    // Reauthenticating the known person (`previous`) rotates the credential
    // like renewal does and keeps the screen lifetime; any other login starts
    // a new one. docs/decisions/2026-09-30-panel-reauthentication-keeps-unsent-work.md
    const epoch = previous ? this.epoch : this.retireScreenLifetime();
    const credentialGeneration = ++this.credentialGeneration;
    const isCurrent = () => epoch === this.epoch && credentialGeneration === this.credentialGeneration;
    const generation = ++this.projectionGeneration;
    this.pendingHydration = null;
    this.renewalKey = null;
    try {
      const response = await this.http.mutate({ operation: 'POST /auth/login', params: {}, body });
      if (!isCurrent()) return { kind: 'unavailable', reason: 'server' };
      if ('membershipSelectionProof' in response.result) {
        if (previous) throw new IdentityHttpError('invalid_response');
        this.membershipSelectionProof = response.result.membershipSelectionProof;
        const listed = await this.http.readLoginMemberships(response.result.membershipSelectionProof);
        if (!isCurrent()) return { kind: 'unavailable', reason: 'server' };
        this.commit({ kind: 'membership_selection_required', personName: listed.result.person_name,
          memberships: listed.result.memberships });
        return { kind: 'membership_selection_required' };
      }
      return await this.acceptLoginResult(response.result, epoch, generation, previous, isCurrent);
    } catch (error) {
      if (!isCurrent()) return { kind: 'unavailable', reason: 'server' };
      // A login answer for another principal or tenant replaces no bearer, but
      // the screen kept for the known person is not handed on either.
      if (previous && isInvalidResponse(error)) this.retireScreenLifetime();
      if (isUnauthorized(error) || error instanceof IdentityHttpError && error.kind === 'invalid_request') return { kind: 'invalid_credentials' };
      if (error instanceof IdentityHttpError && error.status === 429) return { kind: 'too_many_attempts' };
      return { kind: 'unavailable', reason: reasonFor(error) };
    }
  }

  authenticate = (request: CredentialRequest): Promise<AuthenticationOutcome> => this.trackAuthentication(this.login({
    identifier: request.identifier.trim(), password: request.password, rememberMe: request.deviceMode === 'personal',
    ...(request.secondFactor ? { twoFactorCode: request.secondFactor } : {}),
  }, null));

  selectLoginMembership = (membershipId: string): Promise<AuthenticationOutcome> => {
    const proof = this.membershipSelectionProof;
    const selected = this.state.kind === 'membership_selection_required'
      ? this.state.memberships.find((membership) => membership.membership_id === membershipId && membership.status === 'active')
      : undefined;
    if (!proof || !selected) {
      return Promise.resolve({ kind: 'unavailable', reason: 'permission' });
    }
    const task = (async (): Promise<AuthenticationOutcome> => {
      const epoch = this.retireScreenLifetime();
      const credentialGeneration = ++this.credentialGeneration;
      const isCurrent = () => epoch === this.epoch && credentialGeneration === this.credentialGeneration;
      const generation = ++this.projectionGeneration;
      this.pendingHydration = null;
      try {
        const response = await this.http.selectLoginMembership(proof, membershipId);
        if (!isCurrent()) return { kind: 'unavailable', reason: 'server' };
        if (response.result.user.membership_id !== selected.membership_id
          || response.result.restaurant?.id !== selected.restaurant_id
          || response.result.franchise !== null) throw new IdentityHttpError('invalid_response');
        return await this.acceptLoginResult(response.result, epoch, generation, null, isCurrent);
      } catch (error) {
        if (!isCurrent()) return { kind: 'unavailable', reason: 'server' };
        if (isUnauthorized(error)) {
          this.membershipSelectionProof = null;
          this.commit({ kind: 'signed_out' });
          return { kind: 'invalid_credentials' };
        }
        return { kind: 'unavailable', reason: reasonFor(error) };
      }
    })();
    return this.trackAuthentication(task);
  };

  reauthenticate = (request: ReauthenticationRequest): Promise<AuthenticationOutcome> => {
    const previous = this.previousSession();
    if (!previous) return Promise.resolve({ kind: 'unavailable', reason: 'permission' });
    return this.trackAuthentication(this.login({ identifier: previous.identifier, password: request.password,
      ...(previous.tenant.kind === 'restaurant' ? { restaurant_id: previous.tenant.id }
        : previous.tenant.kind === 'franchise' ? { franchise_id: previous.tenant.id } : {}),
      ...(request.secondFactor ? { twoFactorCode: request.secondFactor } : {}),
    }, previous));
  };

  renew = (): Promise<void> => {
    if (this.pendingAuthentication) return this.pendingAuthentication.then(() => undefined);
    if (this.pendingLogout) return this.pendingLogout;
    if (this.pendingCredentialUpdate) return this.pendingCredentialUpdate.then(() => undefined);
    // Rotation does not grant permission to renew a working session. Foreground
    // checks may read its canonical status; expiry still requires password proof.
    if (this.state.kind === 'credential_required' && !this.renewalKey) return this.rehydrateSession();
    if (this.pendingRenewal) return this.pendingRenewal;
    // An expired bearer cannot be renewed (the server answers 401), so a window
    // that ended without being renewed expires instead. A replay key still
    // replays: its first attempt left before the end and may have rotated.
    if (this.state.kind === 'ready' && !this.renewalKey
      && Date.parse(this.state.session.canonical.session.expires_at) <= this.now()) { this.expire(); return Promise.resolve(); }
    const epoch = this.epoch;
    const credentialGeneration = this.credentialGeneration;
    // A password login while this renewal is in flight owns the bearer now.
    const superseded = () => epoch !== this.epoch || credentialGeneration !== this.credentialGeneration;
    const generation = ++this.projectionGeneration;
    this.pendingHydration = null;
    const previous = this.previousSession();
    const idempotencyKey = this.renewalKey ??= this.key();
    const task = (async () => {
      try {
        const response = await this.http.mutate({ operation: 'POST /auth/renew', params: {}, body: {}, idempotencyKey });
        if (superseded()) return;
        this.saveToken(response.result.token);
        this.renewalKey = null;
        try { await this.hydrate(epoch, generation, previous?.canonical.user.id, previous?.tenant); }
        catch (error) { if (epoch === this.epoch && generation === this.projectionGeneration) this.fail(error, 'hydrate', previous); }
      } catch (error) {
        if (superseded()) return;
        // Definitive canonical refusals end recovery. Transport/5xx/invalid
        // responses and retry rate limits retain the original replay key: a
        // prior lost response may already have rotated a successor.
        if (error instanceof IdentityHttpError && error.canonical && error.status !== null
          && [400, 401, 403, 404, 409, 412, 422].includes(error.status)) this.renewalKey = null;
        this.fail(error, 'renew', previous);
      }
    })();
    this.pendingRenewal = task;
    void task.finally(() => { if (this.pendingRenewal === task) this.pendingRenewal = null; });
    return task;
  };

  logout = (): Promise<void> => {
    if (this.pendingLogout) return this.pendingLogout;
    const requestedEpoch = this.epoch;
    const credentialUpdate = this.pendingCredentialUpdate;
    const renewal = this.pendingRenewal ?? (this.renewalKey ? this.renew() : null);
    const task = (async () => {
      // Consume a rotated bearer before revoking its lineage. A stale bearer
      // refusal cannot establish that the renewed session was terminated.
      if (renewal) await renewal;
      if (credentialUpdate) await credentialUpdate;
      if (requestedEpoch !== this.epoch) return;
      // Do not turn an unresolved renewal into a permanently failing logout
      // against its predecessor. Keep the recovery action and error intact.
      if (this.renewalKey) return;
      const previous = this.previousSession();
      const epoch = this.retireScreenLifetime();
      this.pendingHydration = null;
      try {
        await this.http.mutate({ operation: 'POST /auth/logout', params: {}, body: {} });
        if (epoch === this.epoch) this.clearLocalSession();
      } catch (error) { if (epoch === this.epoch) this.fail(error, 'logout', previous); }
    })();
    this.pendingLogout = task;
    void task.finally(() => { if (this.pendingLogout === task) this.pendingLogout = null; });
    return task;
  };

  readEffectiveAccess = async (): Promise<PanelAccessModel> => {
    await this.rehydrateSession();
    const snapshot = this.state;
    if (snapshot.kind === 'unavailable') throw new IdentityHttpError(refusalKind(snapshot.reason));
    if (snapshot.kind !== 'ready') throw new IdentityHttpError('unauthenticated');
    if (snapshot.session.tenant.kind !== 'restaurant') throw new IdentityHttpError('permission');
    return snapshot.session.tenant.access;
  };

  expire = (): void => {
    if (this.state.kind === 'credential_required' && this.state.status === 'required'
      && Date.parse(this.state.credential.canonical.session.expires_at) <= this.now()) {
      this.commit({ kind: 'credential_required', credential: this.state.credential, status: 'expired' });
      return;
    }
    if (this.state.kind === 'ready' && Date.parse(this.state.session.canonical.session.expires_at) <= this.now()) {
      this.commit({ kind: 'expired', session: this.state.session });
    }
  };

  restartLogin = (): void => {
    // The expired identity sheet's "No soy…" is a local abandonment, not a
    // server logout receipt (expired bearers cannot call /auth/logout).
    if ((this.state.kind === 'expired' || this.state.kind === 'credential_required' && this.state.status === 'expired')
      && !this.pendingRenewal && !this.renewalKey) this.clearLocalSession(true);
    else if (this.token) void this.logout();
    else { this.retireScreenLifetime(); this.membershipSelectionProof = null; this.commit({ kind: 'signed_out' }); }
  };

  completeCredentialRotation = (request: CredentialRotationRequest): Promise<CredentialRotationOutcome> => {
    if (this.pendingCredentialUpdate) return this.pendingCredentialUpdate;
    const epoch = this.epoch;
    const task = (async (): Promise<CredentialRotationOutcome> => {
      if (this.interactionBlock) return { kind: 'unavailable', reason: this.interactionBlock === 'offline' ? 'network' : 'verification_pending' };
      if (this.pendingAuthentication) await this.pendingAuthentication;
      if (this.pendingLogout) await this.pendingLogout;
      if (this.pendingRenewal) await this.pendingRenewal;
      if (this.pendingHydration) await this.pendingHydration;
      if (epoch !== this.epoch) return { kind: 'superseded' };
      if (this.interactionBlock) return { kind: 'unavailable', reason: this.interactionBlock === 'offline' ? 'network' : 'verification_pending' };
      const state = this.state;
      if (state.kind !== 'credential_required' || state.status !== 'required') return { kind: 'unavailable', reason: 'permission' };
      if (Date.parse(state.credential.canonical.session.expires_at) <= this.now()) {
        this.expire(); return { kind: 'unavailable', reason: 'permission' };
      }
      if (!request.currentCredential || request.currentCredential.length > 128
        || !/^\d{4,10}$/.test(request.newPin) || !request.idempotencyKey.trim()) {
        return { kind: 'refused', reason: 'invalid_request' };
      }
      try {
        await this.http.mutate({ operation: 'POST /me/credential', params: {},
          body: { current_credential: request.currentCredential, new_pin: request.newPin }, idempotencyKey: request.idempotencyKey });
      } catch (error) {
        if (epoch !== this.epoch) return { kind: 'superseded' };
        if (error instanceof IdentityHttpError && error.canonical && error.status !== null && error.status < 500) {
          return { kind: 'refused', reason: error.status === 401 || error.status === 403 ? 'authorization_denied'
            : error.status === 409 || error.status === 412 ? 'conflict'
              : error.status === 429 ? 'too_many_attempts' : 'invalid_request' };
        }
        // No automatic write retry, no new key, and no claim that the server
        // rejected an operation whose reply was lost or malformed.
        return { kind: 'uncertain', reason: reasonFor(error) };
      }
      if (epoch !== this.epoch) return { kind: 'superseded' };
      const generation = ++this.projectionGeneration;
      try { await this.hydrate(epoch, generation, state.credential.canonical.user.id, state.credential.tenant); }
      catch (error) { if (epoch === this.epoch && generation === this.projectionGeneration) this.fail(error, 'hydrate', state.credential); }
      if (epoch !== this.epoch) return { kind: 'superseded' };
      return this.state.kind === 'ready' ? { kind: 'complete' } : { kind: 'verification_required' };
    })();
    this.pendingCredentialUpdate = task;
    void task.finally(() => { if (this.pendingCredentialUpdate === task) this.pendingCredentialUpdate = null; });
    return task;
  };
}
